import { useEffect, useState } from 'react'
import { History } from 'lucide-react'
import { Link, navigate } from '../router.js'
import { apiRead, type ToastFn } from '../admin/primitives.js'
import { jourFrAnnee } from './lib.js'
import {
  LIBELLE_COMPARER_PRECEDENTE, RAISON_ERREUR, RAISON_RECHERCHE, cheminComparerPrecedente, lecturePrecedente, urlPrecedente,
  type LecturePrecedente, type ReponseComparaison,
} from './comparer-precedente-model.js'

/**
 * « COMPARER À LA PRÉCÉDENTE » (24/10/2026) — le bouton de la fiche soirée.
 *
 * Une lecture par soirée (`/compare?mode=PREVIOUS_COMPARABLE`, la route de la
 * tuile Performance), sans relecture périodique : la précédente ne change que
 * si l'historique change. Trouvée, le bouton est un lien vers la comparaison
 * A/B, et son infobulle nomme la soirée et sa date — la règle prend la même
 * famille avant le même jour de semaine, le nom lève l'ambiguïté. Pendant la
 * lecture, ou sans précédente, il reste à sa place, grisé, avec sa raison
 * (`disabled`, `title`, `aria-label`) : la barre ne saute pas.
 *
 * Aucune écriture, aucune confirmation : une navigation, comme « Comparer ».
 */
export function ComparerPrecedente(props: { id: string }) {
  const { id } = props
  const [lecture, setLecture] = useState<LecturePrecedente | null>(null)
  useEffect(() => {
    let vivant = true
    setLecture(null)
    apiRead<ReponseComparaison>(urlPrecedente(id))
      .then((r) => { if (vivant) setLecture(lecturePrecedente(r.status, r.data, id)) })
      .catch(() => { if (vivant) setLecture({ etat: 'erreur', raison: RAISON_ERREUR }) })
    return () => { vivant = false }
  }, [id])

  if (lecture?.etat === 'trouvee') {
    const p = lecture.precedente
    const quand = p.starts_at ? ` — ${jourFrAnnee(p.starts_at)}` : ''
    return (
      <Link href={cheminComparerPrecedente(id, p.id)} className="lc-btn" title={`Comparer à ${p.name}${quand}`}>
        <History size={14} aria-hidden="true" /> {LIBELLE_COMPARER_PRECEDENTE}
      </Link>
    )
  }
  const raison = lecture === null ? RAISON_RECHERCHE : lecture.raison
  return (
    <button type="button" className="lc-btn" disabled title={raison} aria-label={`${LIBELLE_COMPARER_PRECEDENTE} — ${raison}`}>
      <History size={14} aria-hidden="true" /> {LIBELLE_COMPARER_PRECEDENTE}
    </button>
  )
}

/**
 * Le même geste, sans bouton (pour le menu clic droit, lot 6) : la
 * précédente est lue AU CLIC, puis on y va — ou le toast dit pourquoi.
 * L'adresse de départ est gardée : si l'on a changé d'écran pendant la
 * lecture, on n'arrache personne à l'écran où il est allé.
 */
export async function allerALaPrecedente(id: string, toast: ToastFn): Promise<void> {
  const depart = window.location.pathname + window.location.search
  let l: LecturePrecedente
  try {
    const r = await apiRead<ReponseComparaison>(urlPrecedente(id))
    l = lecturePrecedente(r.status, r.data, id)
  } catch {
    l = { etat: 'erreur', raison: RAISON_ERREUR }
  }
  if (window.location.pathname + window.location.search !== depart) return
  if (l.etat === 'trouvee') navigate(cheminComparerPrecedente(id, l.precedente.id))
  else toast('error', l.raison)
}
